import { useEffect, useSyncExternalStore } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from './api'
import { canOpen, useAccess } from './access'
import { useRefetch } from './connection'

// How many pickers' queries (routes/crew-queries.js) are still open, for the marks that draw the office to them:
// the Dashboard's banner, the Pay row on the rail and the Queries tab. One count for the whole app, read only by
// a sign-in that can open Queries (Timesheets), again on focus and every few minutes. The Queries page sets it
// from its own list, so resolving one clears the marks at once.

const EVERY_MS = 5 * 60_000
const FRESH_MS = 30_000

let count: number | null = null
let readAt = 0
let reading: Promise<void> | null = null
const listeners = new Set<() => void>()

function publish(n: number | null) {
  count = n
  readAt = Date.now()
  listeners.forEach((l) => l())
}

export const setOpenQueries = (n: number) => publish(Math.max(0, n))

async function read(user: Parameters<typeof apiJson>[0], force = false) {
  if (reading || (!force && Date.now() - readAt < FRESH_MS)) return reading ?? undefined
  reading = apiJson<{ open: number }>(user, '/api/queries?status=open')
    .then((r) => publish(typeof r.open === 'number' ? r.open : 0))
    // A mark is a nudge, not a screen: a failed read leaves the last count.
    .catch(() => {})
    .finally(() => {
      reading = null
    })
  return reading
}

/** Open queries, or 0 while unknown or for a sign-in that cannot open Queries. */
export function useOpenQueries(wanted = true): number {
  const { user } = useAuth()
  const access = useAccess()
  const enabled = wanted && !!user && canOpen(access, 'timesheets')
  const n = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => count,
    () => null,
  )
  useEffect(() => {
    if (!enabled) return
    read(user)
    const t = setInterval(() => read(user, true), EVERY_MS)
    return () => clearInterval(t)
  }, [enabled, user])
  useRefetch(() => read(user), enabled)
  return enabled ? n ?? 0 : 0
}

export const openQueriesText = (n: number) => (n === 1 ? '1 open query from a picker' : `${n} open queries from pickers`)

/** The caution mark beside Pay and Queries. */
export function CautionIcon({ title, className }: { title: string; className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label={title} data-testid="queries-caution">
      <title>{title}</title>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  )
}

/** For tests: forget the count. */
export function forgetOpenQueries() {
  count = null
  readAt = 0
  reading = null
}
